
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

interface CustomerInfo {
	firstName: string;
	lastName: string;
	email: string;
	address: string;
	city: string;
	state: string;
	postalCode: string;
	country?: string;
	phone: string;
	userId?: string;
	addressLine2?: string;
}

// Maximum number of retry attempts
const MAX_RETRIES = 3;
// Timeout for each request in milliseconds
const REQUEST_TIMEOUT = 8000;

// Interface for cart item with product details
interface CartItem {
	id: string;
	name: string;
	price: number;
	quantity: number;
	caseQuantity?: number;
	isSample?: boolean;
}

export function useStripeCheckout() {
	const [isLoading, setIsLoading] = useState(false);
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const { user } = useAuth();
	const { toast } = useToast();

	// Helper function to execute a request with timeout and retry logic
	const executeWithRetry = async <T>(
		operation: () => Promise<T>,
		operationName: string,
		retryCount = 0
	): Promise<T | null> => {
		try {
			// Create a timeout promise
			const timeoutPromise = new Promise<null>((_, reject) => {
				setTimeout(() => {
					reject(
						new Error(
							`${operationName} request timed out after ${REQUEST_TIMEOUT}ms`
						)
					);
				}, REQUEST_TIMEOUT);
			});

			// Race the operation against the timeout
			const result = await Promise.race([operation(), timeoutPromise]);
			return result as T;
		} catch (error) {
			console.error(
				`Error in ${operationName} (attempt ${retryCount + 1}):`,
				error
			);

			// If we haven't reached max retries, try again
			if (retryCount < MAX_RETRIES - 1) {
				const backoffTime = Math.pow(2, retryCount) * 1000; // Exponential backoff
				console.log(`Retrying ${operationName} in ${backoffTime}ms...`);

				// Wait before retrying
				await new Promise((resolve) =>
					setTimeout(resolve, backoffTime)
				);

				// Retry the operation
				return executeWithRetry(
					operation,
					operationName,
					retryCount + 1
				);
			}

			// If we've reached max retries, throw the error
			toast({
				title: `${operationName} Failed`,
				description: `Operation could not be completed after multiple attempts. Please try again later.`,
				variant: "destructive",
			});
			return null;
		}
	};

	const createPaymentIntent = async (
		supplierId: string,
		customerInfo: CustomerInfo,
		totalAmount: number,
		items: CartItem[]
	) => {
		if (!user) {
			toast({
				title: "Authentication required",
				description: "You must be logged in to complete checkout",
				variant: "destructive",
			});
			return null;
		}

		try {
			setIsLoading(true);

			// Add user ID to customer info
			const enrichedCustomerInfo = {
				...customerInfo,
				userId: user.id,
			};

			// Call our Supabase Edge Function with retry logic
			const paymentData = await executeWithRetry(
				() =>
					supabase.functions.invoke("create-payment", {
						body: {
							supplierId,
							customerInfo: enrichedCustomerInfo,
							totalAmount,
							items: items.map(item => ({
								id: item.id,
								name: item.name,
								price: item.price,
								quantity: item.quantity,
								caseQuantity: item.caseQuantity || 1,
								isSample: item.isSample || false
							})),
						},
					}),
				"Payment Intent Creation"
			);

			if (!paymentData || paymentData.error) {
				throw new Error(
					paymentData?.error || "Failed to create payment intent"
				);
			}

			setClientSecret(paymentData.data.clientSecret);
			return paymentData.data.clientSecret;
		} catch (error) {
			console.error("Error creating payment intent:", error);
			toast({
				title: "Payment Error",
				description:
					"There was a problem setting up your payment. Please try again.",
				variant: "destructive",
			});
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	const processPaymentTransfer = async (paymentIntentId: string) => {
		try {
			const { data, error } = await supabase.functions.invoke(
				"process-transfer",
				{
					body: {
						paymentIntentId,
					},
				}
			);

			if (error) {
				console.error("Error processing transfer:", error);
				return false;
			}

			return true;
		} catch (error) {
			console.error("Error processing transfer:", error);
			return false;
		}
	};

	return {
		isLoading,
		clientSecret,
		createPaymentIntent,
		processPaymentTransfer,
	};
}
